/**
 * Webhook Delivery Schemas — the vocabularies for inbound provider callbacks.
 *
 * `WebhookDelivery` is the ingestion table that sits underneath the operations
 * timeline: every verified inbound callback lands as a row, so "did the
 * provider actually send it" stops being answerable only from a log search
 * that has already rolled off.
 *
 * A WIRE SCHEMA NOW EXISTS (2026-08-24), because a reader now exists:
 * `GET /v1/webhook-deliveries`. Until then this file was vocabularies only, on
 * the stated grounds that a shape nothing consumes is a guess.
 *
 * ⚠️ **`rawPayload` IS NOT ON THE WIRE SHAPE AND MUST NEVER BE ADDED.** It is
 * the plan's one explicit PII carve-out — byte-verbatim provider bodies, with
 * no redaction, including whole inbound email messages from Mailgun. It exists
 * so a delivery can be re-verified against its signature, which is an
 * offline/forensic operation, not a display field. The reader selects columns
 * explicitly rather than passing the row through, so adding it here would take
 * two deliberate edits — keep it that way.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-08-23-0114-feat-operations-event-spine-plan.md
 */

import { z } from 'zod'

// =============================================================================
// Providers
// =============================================================================

/**
 * Every third party that POSTs to this platform today, one value per handler.
 *
 * Verified against the routes rather than assumed: eleven live under
 * `apps/backend/src/api/_internal/webhooks/`, and `hume` is the outlier,
 * mounted from `_internal/ai-conversations/routes.ts` because it predates that
 * directory.
 *
 * `slack` is still FORWARD-DECLARED: the agent-run ledger plan
 * (docs/_archive/2026-10-17-plans-sweep/plans/2026-08-29-001-feat-agent-run-ledger-event-driven-runs-plan.md)
 * lands its receiver in a later unit, but `AgentRun.source` — added in the
 * same unit as this pair — needs the closed vocabulary to already agree with
 * `WebhookDelivery.provider` so the two never drift once the receiver exists.
 * `granola` is no longer forward-declared — its receiver is live
 * (`apps/backend/src/api/_internal/webhooks/granola/`, verified
 * Standard-Webhooks HMAC; see
 * docs/_archive/2026-10-17-plans-sweep/plans/2026-08-30-0216-feat-granola-webhook-receiver-plan.md). `slack`
 * has no live route yet, so it does not belong in
 * {@link WEBHOOK_DELIVERY_PROVIDERS_PENDING_VERIFICATION} either — that list
 * is for a MOUNTED endpoint that skips signature verification, not for a
 * provider with no endpoint at all.
 *
 * `blackbaud` (SKY Webhook API via Azure Event Grid, SCH-2451) is NOT in the
 * pending-verification list even though Blackbaud signs nothing: its receiver
 * verifies with a constant-time compare of a secret `webhookKey` URL
 * parameter, which is the only proof of origin Blackbaud offers.
 *
 * `zoom` is a listen-only receiver (`_internal/webhooks/zoom/`) that verifies
 * Zoom's `x-zm-signature` HMAC before recording.
 *
 * Closed on purpose, and the reason is the same as the event catalog's: the
 * retention reaper and the operator filters both key on this string, so a
 * provider added by a typo would land rows nothing reaps and nothing shows.
 * Adding a real provider costs one string and no promotion (KD8 — this is a
 * `VARCHAR`, not a DB enum).
 */
export const WEBHOOK_DELIVERY_PROVIDERS = [
  'app_store_connect',
  'expo_eas',
  'github',
  'mux',
  'stream',
  'mailgun_inbound',
  'linear',
  'stripe',
  'blackbaud',
  'hume',
  'granola',
  'slack',
  'zoom'
] as const

export const WebhookDeliveryProviderSchema = z.enum(WEBHOOK_DELIVERY_PROVIDERS)
export type WebhookDeliveryProvider = z.infer<typeof WebhookDeliveryProviderSchema>

export function isWebhookDeliveryProvider(value: string): value is WebhookDeliveryProvider {
  return (WEBHOOK_DELIVERY_PROVIDERS as readonly string[]).includes(value)
}

// =============================================================================
// Statuses
// =============================================================================

/**
 * How far a delivery got.
 *
 * - `verified`  signature checked, not yet handled
 * - `processed` handled, and it changed something
 * - `failed`    the handler threw or refused; the row is the evidence
 * - `ignored`   verified and deliberately not acted on (an event type we have
 *               no processor for), which is a DIFFERENT fact from `failed` and
 *               the one that keeps "we never got it" honest
 *
 * A signature REJECTION is not in this list and never becomes a row: rejecting
 * requires no secret, so persisting rejections would let an unauthenticated
 * caller fill the table by POSTing garbage at a public path. Those stay on the
 * per-handler 401 + `logger.warn` path.
 */
export const WEBHOOK_DELIVERY_STATUSES = ['verified', 'processed', 'failed', 'ignored'] as const

export const WebhookDeliveryStatusSchema = z.enum(WEBHOOK_DELIVERY_STATUSES)
export type WebhookDeliveryStatus = z.infer<typeof WebhookDeliveryStatusSchema>

export function isWebhookDeliveryStatus(value: string): value is WebhookDeliveryStatus {
  return (WEBHOOK_DELIVERY_STATUSES as readonly string[]).includes(value)
}

// =============================================================================
// Which providers may actually be recorded
// =============================================================================

/**
 * Providers that POST to this platform but whose endpoint does NOT yet verify a
 * signature. **No `WebhookDelivery` row may be written for one of these.**
 *
 * R2 is unconditional: inbound deliveries are recorded only AFTER signature
 * verification. `hume` fails it today — its route is mounted without the API-key
 * middleware and carries a standing `TODO: Add webhook signature verification`,
 * so the endpoint is openly writable. Recording it would let an unauthenticated
 * caller fill a 90-day PII-bearing table by POSTing at a public path, which is
 * the same hazard that keeps `webhook.signature_rejected` out of the event
 * catalog — and it would put attacker-supplied bodies in `rawPayload`, the one
 * column with no redaction.
 *
 * It stays in the provider vocabulary because it IS a real inbound webhook and
 * pretending otherwise would just move the surprise to whoever adds it back.
 * The gate is this list, not its absence.
 *
 * DELETE the entry — do not add a bypass — when that endpoint verifies
 * signatures. This list should be empty, and every entry in it is a bug with a
 * ticket, not a design.
 */
export const WEBHOOK_DELIVERY_PROVIDERS_PENDING_VERIFICATION = [
  'hume'
] as const satisfies readonly WebhookDeliveryProvider[]

/**
 * Whether a delivery from this provider may be persisted at all (R2).
 *
 * The recorder calls this before writing; a `false` means the endpoint cannot
 * prove the caller is the provider, so the row would be an unauthenticated
 * write rather than a record of one.
 */
export function canRecordWebhookDelivery(provider: WebhookDeliveryProvider): boolean {
  return !(WEBHOOK_DELIVERY_PROVIDERS_PENDING_VERIFICATION as readonly string[]).includes(provider)
}

// =============================================================================
// Wire shape
// =============================================================================

/**
 * What the delivery resolved to, or `null` for received-but-unroutable.
 *
 * `null` IS MEANINGFUL and is not a missing value: it is the findable state
 * where a provider sent something genuine that named nothing we know. The
 * schema's `resolvedEntityType` docblock says the same thing one level down.
 *
 * `id` is a PUBLIC id or null — never the internal `BigInt` the column stores.
 * A null `id` with a non-null `type` means the row names an entity kind this
 * reader cannot resolve yet; emitting the internal key instead would be an
 * enumerable direct-object reference, which is the exact trade
 * `ListOperationsEventsRequestSchema` refuses one level up.
 */
export const WebhookDeliveryResolvedEntitySchema = z.object({
  type: z.string(),
  id: z.string().nullable(),
  /** Display label (e.g. the app's name), when the reader could resolve one. */
  label: z.string().nullable()
})
export type WebhookDeliveryResolvedEntity = z.infer<typeof WebhookDeliveryResolvedEntitySchema>

/**
 * One inbound delivery, as an operator sees it.
 *
 * `externalId` and `eventTypeRaw` are the PROVIDER's strings, unmapped on
 * purpose — the whole value of this table is answering "what did they actually
 * send", and a normalized copy cannot answer that.
 */
export const WebhookDeliverySchema = z.object({
  /** `WebhookDelivery.publicId`. */
  id: z.string(),
  provider: WebhookDeliveryProviderSchema,
  /** The provider's own id for the event. Half of the idempotency key. */
  externalId: z.string(),
  /** The provider's own event-type string, unmapped. */
  eventTypeRaw: z.string(),
  status: WebhookDeliveryStatusSchema,
  attemptCount: z.number().int(),
  lastError: z.string().nullable(),
  resolvedEntity: WebhookDeliveryResolvedEntitySchema.nullable(),
  correlationId: z.string().nullable(),
  receivedAt: z.string().datetime(),
  processedAt: z.string().datetime().nullable()
})
export type WebhookDelivery = z.infer<typeof WebhookDeliverySchema>

export const WEBHOOK_DELIVERY_PAGE_SIZE_DEFAULT = 50
export const WEBHOOK_DELIVERY_PAGE_SIZE_MAX = 200
